import type { ThemeEmbeddings } from '@shared/utils/search';
import type { StyleKeywordGroups } from './components/Filters/facetOptions.types';
import { styleOptionsForTemplate } from './components/Filters/facetOptions';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import type { ColorHits } from './useColorHits';
import type { TemplateFacetIndex } from './templateFacetMatching.types';

/**
 * How facet selections combine (Jeff, 2026-10-09: "Selecting Magnets and Floral shows
 * everything that's a magnet or a floral. I expected it should be an AND" → "Yes please fix"):
 *
 * - Within one underlying tag group (`color:blue`, `color:red`) the options are OR'd — a
 *   template needs to match any one of them.
 * - Across groups (`style:floral` + `product:magnet`) the groups are AND'd — a template must
 *   satisfy every group that has a selection. "Floral magnets", not "floral or magnet".
 *
 * The group is the part of the composite code before the colon, so two Feature-pill options
 * from different groups (Foil + Magnet) also AND, while Portrait + Landscape OR.
 *
 * Everything here is pure so the filter (`useTagFilteredTemplates`) and the facet counts
 * (`useFilterFacets`) share one definition of "matches". Both read the same
 * `TemplateFacetIndex`, built once per catalog (`useTemplateFacetIndex`) rather than per toggle.
 */

export type FacetSelections = ReadonlyMap<string, ReadonlySet<string>>;

export type FacetMatchContext = Readonly<{
  /** Precomputed theme embeddings; the only source of style matches. `undefined` until loaded. */
  themes: ReadonlyMap<string, ThemeEmbeddings> | undefined;
  /** Style group key → member keywords (`usePrecomputedEmbeddings`); each key is a `style:<key>` option. */
  keywordGroups: StyleKeywordGroups;
  /** `templateId` → color options any of its colorways satisfy (`color:red`…). */
  colorOptionsByTemplateId: ReadonlyMap<string, ReadonlySet<string>>;
}>;

export const groupOfOption = (compositeCode: string) => compositeCode.split(':')[0];

/** Buckets the active composite codes by their underlying group. */
export function groupSelections(activeFilters: ReadonlySet<string>): FacetSelections {
  const groups = new Map<string, Set<string>>();
  for (const option of activeFilters) {
    const group = groupOfOption(option);
    const options = groups.get(group) ?? new Set<string>();
    options.add(option);
    groups.set(group, options);
  }
  return groups;
}

/** `templateId` → the color options its colorways match, from the shared colorway hits. */
export function colorOptionsByTemplateId(hits: ColorHits['all']): ReadonlyMap<string, ReadonlySet<string>> {
  const result = new Map<string, Set<string>>();
  for (const hit of hits) {
    const options = result.get(hit.templateId) ?? new Set<string>();
    options.add(hit.colorOption);
    result.set(hit.templateId, options);
  }
  return result;
}

/**
 * Every facet option a template satisfies.
 *
 * Mapped tags carry the code-owned facets (price, size, photo, foil, orientation, format,
 * product). Style comes off the base theme row in the index, and Colour from the color hits,
 * which are now its only source — do not reintroduce colorway traversal for style.
 *
 * The style read goes through `styleOptionsForTemplate` — the one place a template becomes
 * style options, so the filter and the counts cannot disagree about which index row answers
 * for a suite card.
 */
export function collectTemplateFacetOptions(template: StationeryTemplate, context: FacetMatchContext): ReadonlySet<string> {
  const options = new Set<string>();
  for (const option of template.mappedTags) options.add(option);
  for (const option of styleOptionsForTemplate(template.themeId, context.themes, context.keywordGroups)) options.add(option);
  for (const option of context.colorOptionsByTemplateId.get(template.id) ?? []) options.add(option);
  return options;
}

export function buildTemplateFacetIndex(templates: ReadonlyArray<StationeryTemplate>, context: FacetMatchContext): TemplateFacetIndex {
  return new Map(templates.map(template => [template.id, collectTemplateFacetOptions(template, context)]));
}

const NO_OPTIONS: ReadonlySet<string> = new Set();

const optionsOf = (index: TemplateFacetIndex, template: StationeryTemplate) => index.get(template.id) ?? NO_OPTIONS;

const matchesGroup = (templateOptions: ReadonlySet<string>, groupSelection: ReadonlySet<string>) => {
  for (const option of groupSelection) if (templateOptions.has(option)) return true;
  return false;
};

/** AND across groups, OR within a group. No selection matches everything. */
export function matchesSelections(templateOptions: ReadonlySet<string>, selections: FacetSelections): boolean {
  for (const groupSelection of selections.values()) {
    if (!matchesGroup(templateOptions, groupSelection)) return false;
  }
  return true;
}

/**
 * Per-option match counts, scoped the way faceted search does: an option's count is the
 * number of templates that match it AND every *other* selected group — so with Magnet
 * selected, Style counts become "floral magnets", "beach magnets"…, and the options of the
 * group being refined (Feature, here) still count against the rest of the catalog, so a
 * second Feature choice is never counted out by the first. Every option any template offers
 * is present in the result, with `0` when the current selection excludes it.
 */
export function countFacetOptions(templates: ReadonlyArray<StationeryTemplate>, selections: FacetSelections, index: TemplateFacetIndex): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const template of templates) {
    const templateOptions = optionsOf(index, template);
    const unmatchedGroups: string[] = [];
    for (const [group, groupSelection] of selections) {
      if (!matchesGroup(templateOptions, groupSelection)) unmatchedGroups.push(group);
    }
    for (const option of templateOptions) {
      // The template counts toward an option when the only selected group it fails is the
      // option's own (that group is excluded from its own scope).
      const counted = unmatchedGroups.length === 0 || (unmatchedGroups.length === 1 && unmatchedGroups[0] === groupOfOption(option));
      counts.set(option, (counts.get(option) ?? 0) + (counted ? 1 : 0));
    }
  }
  return counts;
}

export type PartitionedTemplates = Readonly<{
  /** Templates matching every selected group (AND). */
  exact: ReadonlyArray<StationeryTemplate>;
  /**
   * Templates matching at least one selected group but not all (OR minus AND), in catalog
   * order. Empty unless two or more groups are selected — with one group the two sets coincide.
   */
  partial: ReadonlyArray<StationeryTemplate>;
}>;

/**
 * Amy (2026-10-10): "Ideal results chain would be this (so as not to show too few results):
 * 1. FilterA AND FilterB  2. FilterA OR FilterB. So top of results is AND and below that we
 * fill in with OR." The gallery shows `exact` first, then `partial` under a divider.
 */
export function partitionBySelections(templates: ReadonlyArray<StationeryTemplate>, selections: FacetSelections, index: TemplateFacetIndex): PartitionedTemplates {
  const exact: StationeryTemplate[] = [];
  const partial: StationeryTemplate[] = [];
  const fillWithPartial = selections.size > 1;
  for (const template of templates) {
    const templateOptions = optionsOf(index, template);
    let matched = 0;
    for (const groupSelection of selections.values()) if (matchesGroup(templateOptions, groupSelection)) matched++;
    if (matched === selections.size) exact.push(template);
    else if (fillWithPartial && matched > 0) partial.push(template);
  }
  return { exact, partial };
}
